// auditApi — typed fetcher for /v1/audit-logs (Route 6).
import { apiFetch } from "../../lib/api-client.js";

export interface AuditEntry {
  id: string;
  tenant_id: string | null;
  /** Tenant `name` from nexus_control.tenants when available — falls back to
   *  the raw tenant_id ULID. Backend bulk-resolves these on /v1/audit-logs. */
  tenant_name: string | null;
  actor_kind: "platform_user" | "tenant_user" | "officer" | "system";
  actor_id: string | null;
  /** users.display_name from nexus_control when the actor is a real user;
   *  null for system actors and when the user row is missing. */
  actor_display_name: string | null;
  action: string;
  target_kind: string | null;
  target_id: string | null;
  correlation_id: string;
  occurred_at: string;
  diff: string | null;
  /** Set when this row was written by an AI plan-apply op. All rows from
   *  one Plan & Apply batch share the same value; the audit page groups
   *  them under a single「AI 計畫」 banner. NULL on manual / cron writes. */
  plan_correlation_id: string | null;
}

export interface AuditListResponse {
  items: AuditEntry[];
  total: number;
  next_cursor: string | null;
  correlation_id: string | null;
}

export class AuditApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

export function friendlyAuditError(err: unknown): string {
  if (err instanceof AuditApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色看不到稽核紀錄。";
    if (err.status === 400 && err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
    if (err.status === 400 && err.code === "invalid_range")
      return "起訖日期不對，請確認結束日期晚於開始日期。";
    return "稽核紀錄暫時無法載入。";
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<AuditApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
  } catch {}
  return new AuditApiError(r.status, code, cid);
}

export interface AuditQuery {
  tenantId?: string | null;
  q?: string | null;
  from?: string | null;
  to?: string | null;
  limit?: number | null;
  offset?: number | null;
}

export async function listAudit(params: AuditQuery = {}): Promise<AuditListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.q) qs.set("q", params.q);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const r = await apiFetch(`/v1/audit-logs${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AuditListResponse;
}
